import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { initials } from "@/lib/meta";
import api from "@/lib/api";
import { toast } from "sonner";
import { Lock, Eye, EyeOff, User, ShieldCheck } from "lucide-react";

export default function ProfileDialog({ open, setOpen, user, onUpdated }) {
  const [form, setForm] = useState({
    name: "",
    job_title: "",
    avatar_url: "",
    current_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && user) {
      setForm({
        name: user.name || "",
        job_title: user.job_title || "",
        avatar_url: user.avatar_url || "",
        current_password: "",
        new_password: "",
        confirm_password: "",
      });
    }
  }, [open, user]);

  if (!user) return null;

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }

    if (form.new_password || form.confirm_password) {
      if (form.new_password.length < 6) {
        toast.error("New password must be at least 6 characters");
        return;
      }
      if (form.new_password !== form.confirm_password) {
        toast.error("New passwords do not match");
        return;
      }
    }

    setSaving(true);
    try {
      const payload = {
        name: form.name,
        job_title: form.job_title,
        avatar_url: form.avatar_url,
      };

      if (form.new_password) {
        payload.password = form.new_password;
        if (form.current_password) {
          payload.current_password = form.current_password;
        }
      }

      await api.patch(`/users/${user.id}`, payload);
      toast.success(form.new_password ? "Profile & password updated successfully!" : "Profile updated successfully!");
      setOpen(false);
      
      // Update local stored user details & refresh
      const meRes = await api.get("/auth/me");
      localStorage.setItem("ft_user", JSON.stringify(meRes.data));
      onUpdated?.(meRes.data);
      window.location.reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="profile-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2 text-xl">
            <User className="w-5 h-5 text-primary" /> Profile & Security
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* User Header card */}
          <div className="flex items-center gap-4 p-3.5 border rounded-lg bg-muted/20">
            <Avatar className="w-14 h-14 border">
              <AvatarImage src={form.avatar_url || user.avatar_url} />
              <AvatarFallback className="text-base font-semibold">{initials(user.name)}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-base truncate">{user.name}</div>
              <div className="text-xs text-muted-foreground truncate">{user.email}</div>
              <div className="mt-1 inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-wider text-primary bg-primary/10 px-2 py-0.5 rounded-full font-medium">
                <ShieldCheck className="w-3 h-3" /> {user.role}
              </div>
            </div>
          </div>

          {/* Profile details */}
          <div className="space-y-3">
            <div className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">General Information</div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Full Name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="profile-name" />
              </div>
              <div className="space-y-1.5">
                <Label>Job Title</Label>
                <Input value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })} placeholder="e.g. Software Engineer" data-testid="profile-job" />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Avatar Image URL</Label>
              <Input value={form.avatar_url} onChange={(e) => setForm({ ...form, avatar_url: e.target.value })} placeholder="https://..." data-testid="profile-avatar" />
            </div>
          </div>

          {/* Password Section */}
          <div className="space-y-3 pt-2 border-t">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-muted-foreground">
              <Lock className="w-3.5 h-3.5" /> Change Password
            </div>

            <div className="space-y-3 p-3.5 border rounded-lg bg-muted/10">
              <div className="space-y-1.5">
                <Label className="text-xs">Current Password</Label>
                <div className="relative">
                  <Input
                    type={showCurrent ? "text" : "password"}
                    value={form.current_password}
                    onChange={(e) => setForm({ ...form, current_password: e.target.value })}
                    placeholder="Enter current password"
                    data-testid="profile-current-pw"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrent(!showCurrent)}
                    className="absolute right-2.5 top-2.5 text-muted-foreground hover:text-foreground"
                  >
                    {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">New Password</Label>
                  <div className="relative">
                    <Input
                      type={showNew ? "text" : "password"}
                      value={form.new_password}
                      onChange={(e) => setForm({ ...form, new_password: e.target.value })}
                      placeholder="Min 6 characters"
                      data-testid="profile-new-pw"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNew(!showNew)}
                      className="absolute right-2.5 top-2.5 text-muted-foreground hover:text-foreground"
                    >
                      {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs">Confirm New Password</Label>
                  <Input
                    type="password"
                    value={form.confirm_password}
                    onChange={(e) => setForm({ ...form, confirm_password: e.target.value })}
                    placeholder="Confirm new password"
                    data-testid="profile-confirm-pw"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="pt-2">
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="profile-save">
            {saving ? "Saving..." : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
